import { logEvent, type Analytics } from 'firebase/analytics';
import { analytics } from '@/firebase-config';
import { logger } from '@/utils/logger';

export const BETA_ANALYTICS_EVENT_NAMES = [
    'beta_project_created',
    'beta_script_import_started',
    'beta_script_import_completed',
    'beta_script_import_failed',
    'beta_member_invite_sent',
    'beta_schedule_event_created',
    'beta_email_sent',
    'beta_report_printed',
    'beta_file_uploaded',
    'beta_prompt_book_opened',
    'beta_barry_opened',
    'beta_active_project_day',
] as const;

export type BetaAnalyticsEventName = typeof BETA_ANALYTICS_EVENT_NAMES[number];

type BetaAnalyticsParamValue = string | number | boolean;
type BetaAnalyticsParams = Record<string, unknown>;
type SanitizedBetaAnalyticsParams = Record<string, BetaAnalyticsParamValue>;

const SAFE_STRING_VALUE_PATTERN = /^[a-z0-9][a-z0-9_:-]{0,63}$/i;

const EVENT_PARAM_ALLOWLIST: Record<BetaAnalyticsEventName, readonly string[]> = {
    beta_project_created: ['source', 'is_demo'],
    beta_script_import_started: ['source', 'script_text_access_enabled'],
    beta_script_import_completed: ['duration_bucket', 'source'],
    beta_script_import_failed: ['error_category', 'source'],
    beta_member_invite_sent: ['role_category', 'source'],
    beta_schedule_event_created: ['event_type', 'source'],
    beta_email_sent: ['template_type', 'recipient_count_bucket'],
    beta_report_printed: ['report_type', 'source'],
    beta_file_uploaded: ['mime_category', 'size_bucket', 'visibility', 'access_policy'],
    beta_prompt_book_opened: ['has_script', 'source'],
    beta_barry_opened: ['source'],
    beta_active_project_day: ['source', 'segment'],
};

export function isBetaAnalyticsEventName(name: string): name is BetaAnalyticsEventName {
    return BETA_ANALYTICS_EVENT_NAMES.includes(name as BetaAnalyticsEventName);
}

function isSafeParamValue(value: unknown): value is BetaAnalyticsParamValue {
    if (typeof value === 'boolean') return true;
    if (typeof value === 'number') return Number.isFinite(value);
    return typeof value === 'string' && SAFE_STRING_VALUE_PATTERN.test(value);
}

export function sanitizeBetaAnalyticsParams(
    eventName: BetaAnalyticsEventName,
    params: BetaAnalyticsParams = {}
): SanitizedBetaAnalyticsParams {
    const allowedKeys = new Set(EVENT_PARAM_ALLOWLIST[eventName]);
    const sanitized: Record<string, BetaAnalyticsParamValue> = {};

    for (const [key, value] of Object.entries(params)) {
        if (!allowedKeys.has(key) || value === null || value === undefined) continue;
        if (!isSafeParamValue(value)) continue;
        sanitized[key] = value;
    }

    return sanitized;
}

function getDefaultAnalyticsClient(): Analytics | null {
    if (import.meta.env.MODE === 'test' || process.env.NODE_ENV === 'test') {
        return null;
    }

    return analytics;
}

function recordE2EAnalyticsEvent(
    eventName: BetaAnalyticsEventName,
    params: SanitizedBetaAnalyticsParams
): boolean {
    if (import.meta.env.VITE_E2E !== 'true' || typeof window === 'undefined') {
        return false;
    }

    window.__ON_BOOK_E2E_ANALYTICS__?.record({ name: eventName, params });
    return Boolean(window.__ON_BOOK_E2E_ANALYTICS__);
}

export function logBetaAnalyticsEvent(
    eventName: string,
    params: BetaAnalyticsParams = {},
    analyticsClient?: Analytics | null
): boolean {
    if (!isBetaAnalyticsEventName(eventName)) {
        logger.warn(`[Beta Analytics] Blocked unknown event name: ${eventName}`);
        return false;
    }

    const sanitizedParams = sanitizeBetaAnalyticsParams(eventName, params);
    const resolvedAnalyticsClient = analyticsClient === undefined ? getDefaultAnalyticsClient() : analyticsClient;

    if (recordE2EAnalyticsEvent(eventName, sanitizedParams)) {
        return true;
    }

    if (!resolvedAnalyticsClient) {
        return false;
    }

    try {
        logEvent(resolvedAnalyticsClient, eventName, sanitizedParams);
        return true;
    } catch (error: unknown) {
        logger.warn('[Beta Analytics] Event logging failed:', error);
        return false;
    }
}

function localDateKey(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function logBetaActiveProjectDay(
    projectId: string,
    params: Partial<Record<'source' | 'segment', unknown>> = {},
    analyticsClient?: Analytics | null
): boolean {
    if (typeof window === 'undefined' || !projectId) return false;

    const storageKey = `onbook:beta-active-project-day:${projectId}:${localDateKey()}`;

    try {
        if (window.localStorage.getItem(storageKey)) return false;
    } catch (error: unknown) {
        logger.warn('[Beta Analytics] Active-day dedupe unavailable:', error);
    }

    const logged = logBetaAnalyticsEvent('beta_active_project_day', {
        source: 'app_open',
        ...params,
    }, analyticsClient);

    if (logged) {
        try {
            window.localStorage.setItem(storageKey, '1');
        } catch (error: unknown) {
            logger.warn('[Beta Analytics] Active-day dedupe write failed:', error);
        }
    }

    return logged;
}

export function toRecipientCountBucket(count: number): string {
    if (count <= 0) return '0';
    if (count === 1) return '1';
    if (count <= 5) return '2_5';
    if (count <= 10) return '6_10';
    if (count <= 25) return '11_25';
    if (count <= 50) return '26_50';
    return '51_plus';
}

export function toFileSizeBucket(bytes: number): string {
    if (bytes < 1024 * 1024) return 'under_1mb';
    if (bytes < 10 * 1024 * 1024) return '1_10mb';
    if (bytes < 50 * 1024 * 1024) return '10_50mb';
    if (bytes < 200 * 1024 * 1024) return '50_200mb';
    return '200mb_plus';
}

export function toDurationBucket(milliseconds: number): string {
    if (!Number.isFinite(milliseconds) || milliseconds < 0) return 'unknown';
    if (milliseconds < 30_000) return 'under_30s';
    if (milliseconds < 60_000) return '30_60s';
    if (milliseconds < 3 * 60_000) return '1_3m';
    if (milliseconds < 10 * 60_000) return '3_10m';
    if (milliseconds < 30 * 60_000) return '10_30m';
    return '30m_plus';
}

export function toMimeCategory(mimeType?: string): string {
    if (!mimeType) return 'unknown';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.includes('csv')) return 'csv';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'document';
    return 'other';
}

export function toErrorCategory(error?: string): string {
    const normalized = (error ?? '').toLowerCase();
    if (normalized.includes('permission') || normalized.includes('unauthorized')) return 'permission';
    if (normalized.includes('timeout') || normalized.includes('deadline')) return 'timeout';
    if (normalized.includes('network') || normalized.includes('unavailable')) return 'unavailable';
    if (normalized.includes('validation') || normalized.includes('invalid')) return 'validation';
    if (normalized.includes('quota') || normalized.includes('rate')) return 'quota';
    return 'processing';
}

export function toRoleCategory(roleId?: string): string {
    const normalized = (roleId ?? '').toLowerCase();
    if (normalized.includes('owner') || normalized.includes('admin')) return 'admin';
    if (normalized.includes('stage') || normalized.includes('manager') || normalized.includes('sm')) return 'production';
    if (normalized.includes('cast') || normalized.includes('actor')) return 'cast';
    if (normalized.includes('crew') || normalized.includes('designer')) return 'crew';
    return 'collaborator';
}
